import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import DynamicList from "./List";

interface TabProps {
  title: string;
  tabs: TabItemProps[];
  leftSidebar?: React.ReactNode;
  onTitleClick?: () => void;
  titleClassName?: string;
  tabButtonClassName?: string;
  rootClassName?: string;
  className?: string;
  navClassName?: string;
  tabListClassName?: string;
  tabActiveClassName?: string;
  tabInactiveClassName?: string;
}

interface TabState {
  searchTerm: string;
  currentPage: number;
  viewMode?: "list" | "grid";
}

export interface TabItemProps {
  type: "list" | "grid" | "custom";
  title: string;
  actions?: React.ReactNode;
  isSearch?: boolean;
  isPagination?: boolean;
  path: string;
  fetcher?: (page: number, searchTerm?: string) => Promise<any>;
  tabId?: string;
  errorMessage?: string;
  emptyMessage?: string;
  item?: (data: any, viewMode?: string) => React.ReactNode;
  leftArea?: () => React.ReactNode;
  showToggle?: boolean;
  dataProcessor?: (data: any[], searchTerm?: string) => any[];
  customComponent?: React.ReactNode;
  clientPageSize?: number;
  clientPaginateByRootLevelZero?: boolean;
  refreshKey?: string | number;
  /** Süzgeç anahtarı: değişince bu sekmenin sayfası 1'e döner (süzülmüş listede boş sayfada kalınmasın) */
  filterKey?: string;
  gridClassName?: string;
  onClick?: () => void;
}

function TabItem({
  type,
  title,
  actions,
  isSearch = false,
  isPagination = true,
  path,
  fetcher,
  errorMessage = "Veriler yüklenirken bir hata oluştu",
  emptyMessage = "Henüz veri bulunamadı.",
  item,
  leftArea,
  showToggle = false,
  dataProcessor,
  customComponent,
  clientPageSize,
  clientPaginateByRootLevelZero,
  refreshKey,
  gridClassName,
  tabState,
  onSearchChange,
  onPageChange,
  onViewModeChange,
}: TabItemProps & {
  tabState?: TabState;
  onSearchChange?: (term: string) => void;
  onPageChange?: (page: number) => void;
  onViewModeChange?: (mode: "list" | "grid") => void;
}) {
  if (type === "custom" && customComponent) {
    return (
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto md:overflow-y-auto custom-scrollbar">
        {customComponent}
      </div>
    );
  }

  const listType = type === "custom" ? "list" : type;

  return (
    <DynamicList
      key={refreshKey}
      type={listType}
      header={false}
      title={title}
      path={path}
      fetcher={fetcher}
      refreshKey={refreshKey}
      isSearch={isSearch}
      isPagination={isPagination}
      errorMessage={errorMessage}
      emptyMessage={emptyMessage}
      actions={actions}
      item={item || (() => <div>No item renderer</div>)}
      leftArea={leftArea}
      showToggle={showToggle}
      dataProcessor={dataProcessor}
      clientPageSize={clientPageSize}
      clientPaginateByRootLevelZero={clientPaginateByRootLevelZero}
      externalSearchTerm={tabState?.searchTerm}
      externalCurrentPage={tabState?.currentPage}
      onSearchChange={onSearchChange}
      onPageChange={onPageChange}
      externalViewMode={tabState?.viewMode}
      onViewModeChange={onViewModeChange}
      gridClassName={gridClassName}
    />
  );
}

export default function TabPage({
  title,
  tabs,
  leftSidebar,
  onTitleClick,
  titleClassName = '',
  tabButtonClassName = '',
  rootClassName,
  className = '',
  navClassName = '',
  tabListClassName = '',
  tabActiveClassName,
  tabInactiveClassName,
}: TabProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState<TabItemProps>();
  const [tabStates, setTabStates] = useState<Map<string, TabState>>(new Map());

  const getTabKey = (tab: TabItemProps) => tab.tabId || tab.path;

  const getTabState = (tab: TabItemProps): TabState => {
    const key = getTabKey(tab);
    if (!tabStates.has(key)) {
      return {
        searchTerm: "",
        currentPage: 1,
        viewMode: tab.type === "grid" ? "grid" : "list",
      };
    }
    return tabStates.get(key)!;
  };

  const updateTabState = (tab: TabItemProps, updater: Partial<TabState>) => {
    const key = getTabKey(tab);
    const currentState = getTabState(tab);
    setTabStates(
      new Map(tabStates).set(key, {
        ...currentState,
        ...updater,
      })
    );
  };

  // Süzgeç değişince sekmenin sayfası 1'e döner (yalnız filterKey tanımlayan sekmeler)
  const lastFilterKey = useRef<{ tab?: string; key?: string }>({});
  useEffect(() => {
    if (!currentTab || currentTab.filterKey === undefined) return;
    const tabKey = getTabKey(currentTab);
    const previous = lastFilterKey.current;
    lastFilterKey.current = { tab: tabKey, key: currentTab.filterKey };
    if (previous.tab === tabKey && previous.key !== currentTab.filterKey && getTabState(currentTab).currentPage !== 1) {
      updateTabState(currentTab, { currentPage: 1 });
    }
  }, [currentTab?.filterKey]);

  useEffect(() => {
    if (tabs && tabs.length > 0) {
      const params = new URLSearchParams(location.search);
      const tabParam = params.get("tab");
      const initialTab = tabs.find((t) => (t.tabId || t.path) === tabParam);

      setCurrentTab((prev) => {
        if (initialTab) return initialTab;
        if (!prev) return tabs[0];
        const foundByTabId = tabs.find((t) => (t.tabId || t.path) === (prev.tabId || prev.path));
        if (foundByTabId) return foundByTabId;
        const foundByTitle = tabs.find((t) => t.title === prev.title);
        return foundByTitle || tabs[0];
      });
    }
  }, [tabs, location.search]);

  const renderTabButtonItem = (_tab: TabItemProps) => {
    const activeCls = tabActiveClassName ?? "text-blue-600 font-medium border-b-2 border-blue-500";
    const inactiveCls = tabInactiveClassName ?? "hover:text-gray-900";
    return (
      <button
        key={_tab.path}
        className={`pb-3 transition-all duration-200 flex-shrink-0 ${tabButtonClassName} ${currentTab === _tab ? activeCls : inactiveCls}`}
        onClick={() => {
          _tab.onClick?.();
          setCurrentTab(_tab);
          navigate(`?tab=${_tab.tabId || _tab.path}`, { replace: true });
        }}>
        {_tab.title}
      </button>
    );
  };

  const emptyItem = (data: any) => {
    return <h1>Deneme</h1>;
  };
  const rootBg = rootClassName ?? "bg-gray-50";
  return (
    <div
      className={`min-h-[calc(100dvh-4rem)] flex overflow-y-auto md:h-[calc(100dvh-4rem)] md:overflow-hidden ${rootBg} ${className}`.trim()}
    >
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <nav className={`flex-shrink-0 ${navClassName}`.trim()}>
          <div className="px-4 sm:px-6 py-3">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
              <div className="flex items-center gap-2">
                {onTitleClick ? (
                  <button
                    type="button"
                    onClick={onTitleClick}
                    className={`font-semibold text-gray-900 transition-colors hover:text-blue-600 ${titleClassName}`}
                  >
                    {title}
                  </button>
                ) : (
                  <span className={`font-semibold text-gray-900 ${titleClassName}`}>{title}</span>
                )}
              </div>
              <div
                className={`flex items-center gap-4 sm:gap-6 text-sm text-gray-600 overflow-x-auto scrollbar-hide w-full sm:w-auto -mx-2 px-2 whitespace-nowrap ${tabListClassName}`.trim()}
              >
                {tabs.map((_tab) => renderTabButtonItem(_tab))}
              </div>
            </div>
          </div>
        </nav>
        <div className="flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto md:overflow-hidden">
          {currentTab && (
            <TabItem
              type={currentTab?.type || "list"}
              title={currentTab?.title || ""}
              actions={currentTab?.actions}
              isSearch={currentTab?.isSearch ?? false}
              isPagination={currentTab?.isPagination || true}
              path={currentTab?.path || "health"}
              fetcher={currentTab?.fetcher as any}
              errorMessage={currentTab?.errorMessage || ""}
              emptyMessage={currentTab?.emptyMessage || ""}
              item={currentTab?.item || emptyItem}
              leftArea={currentTab?.leftArea || (leftSidebar ? () => leftSidebar : undefined)}
              showToggle={currentTab?.showToggle || false}
              dataProcessor={currentTab?.dataProcessor}
              customComponent={currentTab?.customComponent}
              clientPageSize={currentTab?.clientPageSize}
              clientPaginateByRootLevelZero={currentTab?.clientPaginateByRootLevelZero}
              refreshKey={currentTab?.refreshKey}
              gridClassName={currentTab?.gridClassName}
              tabState={getTabState(currentTab)}
              onSearchChange={(term) => {
                updateTabState(currentTab, {
                  searchTerm: term,
                  currentPage: 1,
                });
              }}
              onPageChange={(page) => {
                updateTabState(currentTab, { currentPage: page });
              }}
              onViewModeChange={(mode) => {
                updateTabState(currentTab, { viewMode: mode });
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
